<template lang="pug">
    .carlie-details(:class="{ 'carlie-details-live': isLiveHandoff && details }")
        .text-body-2.mb-2(v-if="contextDescription") {{ contextDescription }}

        template(v-if="isLoading")
            .text-center.py-6
                v-progress-circular(color="var(--carlie-purple)" indeterminate)

        v-alert(v-else-if="errorMessage" type="error" outlined dense border="left")
            | {{ errorMessage }}

        template(v-else-if="details")
            //- On a live transfer the agent is already talking to the caller, so the tab leads with
            //- the same brief the offer showed and leaves out the review sections.
            template(v-if="isLiveHandoff")
                CarlieOfferBrief(
                    :analysis="analysis"
                    empty-text="Carlie did not leave notes for this call."
                    :handoff="details.handoff"
                )
                .text-caption.text--secondary.mt-1.mb-3(v-if="isAnalysisPending") More from Carlie's analysis is on the way.
                .mb-3(v-else)

            v-alert(v-if="isEscalated && !isLiveHandoff" icon="mdi-account-alert" type="error" outlined dense border="left")
                .font-weight-bold Escalated customer
                .text-body-2(v-if="escalationSummary") {{ escalationSummary }}

            .tags.mb-3(v-if="hasTags && !isLiveHandoff")
                v-tooltip(v-if="sentimentDisplay" bottom max-width="320" :disabled="!sentimentSummary")
                    template(v-slot:activator="{ on, attrs }")
                        v-chip(small outlined :color="sentimentDisplay.color" v-bind="attrs" v-on="on")
                            v-icon(left small) {{ sentimentDisplay.icon }}
                            | {{ sentimentDisplay.label }}
                    span {{ sentimentSummary }}
                v-chip(v-if="primaryTopic" small color="var(--carlie-purple-10)")
                    v-icon(left small color="var(--carlie-purple)") mdi-tag
                    | {{ primaryTopic.label }}
                v-chip(v-for="topic in secondaryTopics" :key="topic.key" small outlined)
                    v-icon(left small) mdi-tag-outline
                    | {{ topic.label }}
                v-chip(v-if="handoffReason" small outlined)
                    v-icon(left small) mdi-phone-forward
                    | {{ handoffReason }}

            v-alert(v-if="details.handoff_status === 'unavailable'" type="warning" outlined dense border="left")
                | Carlie handoff details are unavailable right now.
            v-alert(v-else-if="details.handoff_status === 'none'" type="info" outlined dense border="left")
                | Carlie did not record handoff details for this call.

            v-alert(v-if="analysis && analysis.agent_adherence_issue && !isLiveHandoff" icon="mdi-headset" type="warning" outlined dense border="left")
                .font-weight-bold Process adherence issue

            v-list.py-0(v-if="fields.length > 0" dense)
                v-list-item.px-0(v-for="field in fields" :key="field.label")
                    v-list-item-icon.mr-3
                        v-icon(small color="var(--carlie-purple)") {{ field.icon }}
                    v-list-item-content
                        v-list-item-subtitle {{ field.label }}
                        .text-body-2 {{ field.value }}

            template(v-if="handoffSummary && !isLiveHandoff")
                .section-title HANDOFF SUMMARY
                v-alert.mb-0(dense border="left" color="var(--carlie-purple-10)" icon="mdi-account-arrow-right")
                    ul.summary-list
                        li(v-for="(sentence, index) in splitSentences(handoffSummary)" :key="index") {{ sentence }}

            template(v-else-if="summary && !isLiveHandoff")
                .section-title
                    span SUMMARY
                    v-chip.ml-2(v-if="isAnalysisPending" x-small outlined) Analysis pending
                v-alert.mb-0(dense border="left" color="var(--off-white)" icon="mdi-information")
                    ul.summary-list
                        li(v-for="(sentence, index) in splitSentences(summary)" :key="index") {{ sentence }}

            template(v-if="alerts.length > 0 && !isLiveHandoff")
                .section-title ALERTS TRIGGERED
                v-chip.mr-2.mb-2(v-for="(alert, index) in alerts" :key="index" small outlined color="var(--error)")
                    v-icon(left small) mdi-bell-alert-outline
                    | {{ alert }}

            template(v-if="promises.length > 0")
                .section-title PROMISES MADE
                .d-flex.align-start.mb-1(v-for="promise in promises" :key="promise.vid")
                    v-icon.mr-2(small color="var(--carlie-purple)") mdi-handshake-outline
                    .text-body-2 {{ promise.summary }}

            template(v-if="recordingUrl && !isLiveHandoff")
                .section-title RECORDING
                VolieAudio(:file="recordingUrl" color="success")

            template(v-if="turns.length > 0")
                .section-title CONVERSATION
                //- A live transfer opens on the last turns, which say why Carlie handed off.
                div(ref="conversation" :class="{ 'conversation-live': isLiveHandoff }")
                    template(v-for="turn in turns")
                        .text-caption.text--secondary.text-center.my-2(v-if="turn.role === 'handoff'" :key="turn.sequence_num")
                            | {{ turn.content }}
                        .turn(v-else :key="turn.sequence_num" :class="'turn-' + turn.role")
                            .d-flex.align-center.text-caption.text--secondary.mb-1
                                img.turn-logo.mr-1(v-if="turn.role === 'assistant'" :src="carlieLogo" alt="Carlie")
                                span.font-weight-bold.mr-2 {{ turnSpeaker(turn.role) }}
                                span {{ formatTurnTime(turn.timestamp_ms) }}
                            .text-body-2 {{ turn.content }}
</template>

<script lang="ts">
import Vue from 'vue';

// Assets
import carlieLogo from '@/components/carlie/carlie_logo.png';

// Components
import CarlieOfferBrief from '@/components/carlie/carlie_offer_brief.vue';
import VolieAudio from '@/components/shared/audio_player.vue';

// Data
import { GetAiCommunicationCarlieDetails } from '@/data';

// Helpers
import {
    CARLIE_CONTEXT_DROPPED_AFTER_TRANSFER,
    CARLIE_CONTEXT_LIVE_HANDOFF,
    carlieContextDescription,
    carlieHandoffReasonLabel,
    carlieHandoffSummary,
    carlieHeadline,
    carlieItemSummary,
    carliePromises,
    carlieSummary,
} from '@/helpers/carlie';
import { formatTel, titleize } from '@/helpers/general';
const TURN_SPEAKERS = {
    agent: "Agent",
    assistant: "Carlie",
    user: "Customer",
};

export default Vue.extend({
    data() {
        return {
            carlieLogo,
            details: null,
            errorMessage: null,
            isLoading: false,
        }
    },
    methods: {
        formatTurnTime(timestampMs: number): string {
            const totalSeconds = Math.max(0, Math.floor((timestampMs || 0) / 1000));
            const seconds = (totalSeconds % 60).toString().padStart(2, "0");
            return `${Math.floor(totalSeconds / 60)}:${seconds}`;
        },
        getCarlieDetails() {
            if (!(this.aiCommunicationId > 0)) {
                this.details = null;
                return;
            }

            const aiCommunicationId = this.aiCommunicationId;
            this.errorMessage = null;
            this.isLoading = true;

            GetAiCommunicationCarlieDetails(aiCommunicationId).then((response) => {
                if (aiCommunicationId !== this.aiCommunicationId) {
                    return;
                }
                if (response?.Error) {
                    this.errorMessage = response.Error;
                    this.details = null;
                    return;
                }
                this.details = response?.Data || null;
            }).catch(() => {
                this.errorMessage = "Unable to get Carlie details";
                this.details = null;
            }).finally(() => {
                if (aiCommunicationId === this.aiCommunicationId) {
                    this.isLoading = false;
                }
            });
        },
        load() {
            if (this.initialDetails) {
                this.details = this.initialDetails;
                this.errorMessage = null;
                return;
            }
            this.getCarlieDetails();
        },
        scrollConversationToEnd() {
            this.$nextTick(() => {
                const conversation = this.$refs.conversation as HTMLElement;
                if (conversation) {
                    conversation.scrollTop = conversation.scrollHeight;
                }
            });
        },
        // Summaries are written as one paragraph, so each sentence becomes a bullet.
        splitSentences(text: string): string[] {
            return text.replace(/([.!?])\s+(?=[A-Z0-9"])/g, "$1\n")
                .split("\n")
                .map((sentence) => sentence.trim())
                .filter((sentence) => !!sentence);
        },
        turnSpeaker(role: string): string {
            return TURN_SPEAKERS[role] || titleize(role);
        },
    },
    computed: {
        alerts(): string[] {
            return (this.analysis?.alerts || []).map((alert) => alert.name || titleize(alert.purpose));
        },
        analysis(): any {
            return this.liveAnalysis || this.details?.analysis || null;
        },
        // Only a caller who hung up waiting for an agent needs saying; the tab label covers the rest.
        contextDescription(): string {
            if (this.context !== CARLIE_CONTEXT_DROPPED_AFTER_TRANSFER) {
                return "";
            }
            return `${carlieContextDescription(this.context)}.`;
        },
        // The brief already carries the callback number and preferred window on a live transfer, and
        // the intent too when it stands in for the headline. The handoff reason has no tag row to
        // sit in, so it moves here.
        fields(): Array<{ icon: string, label: string, value: string }> {
            const handoff = this.details?.handoff;

            if (this.isLiveHandoff) {
                const intentIsHeadline = carlieHeadline(this.analysis, handoff).fromIntent;
                return [
                    { icon: "mdi-account", label: "Caller", value: handoff?.caller_name },
                    { icon: "mdi-target", label: "Intent", value: intentIsHeadline ? "" : handoff?.caller_intent },
                    { icon: "mdi-phone-forward", label: "Why Carlie transferred", value: this.handoffReason },
                ].filter((field) => !!field.value);
            }

            return [
                { icon: "mdi-account", label: "Caller", value: handoff?.caller_name },
                { icon: "mdi-phone-return", label: "Callback Number", value: handoff?.caller_callback_number ? formatTel(handoff.caller_callback_number) : "" },
                { icon: "mdi-clock-outline", label: "Preferred Callback", value: handoff?.preferred_callback_window },
                { icon: "mdi-target", label: "Intent", value: handoff?.caller_intent },
            ].filter((field) => !!field.value);
        },
        escalation(): any {
            return this.analysis?.escalation || null;
        },
        escalationSummary(): string {
            return carlieItemSummary(this.escalation?.summary);
        },
        handoffReason(): string {
            return carlieHandoffReasonLabel(this.details?.handoff?.handoff_reason);
        },
        handoffSummary(): string {
            return carlieHandoffSummary(this.analysis);
        },
        hasTags(): boolean {
            return !!this.sentimentDisplay || !!this.primaryTopic || this.secondaryTopics.length > 0 || !!this.handoffReason;
        },
        // A live transfer is usually accepted before its analysis is enqueued, so no status yet still
        // means pending when the server says one is coming.
        isAnalysisPending(): boolean {
            const status = this.analysis?.status;
            if (["pending", "in_progress"].includes(status)) {
                return true;
            }
            return this.isLiveHandoff && !status && this.analysis?.analysis_expected === true;
        },
        // Carlie's escalation is the customer losing their calm with the AI, not Pulse's escalated customer step.
        isEscalated(): boolean {
            return !!this.escalation?.is_flagged;
        },
        isLiveHandoff(): boolean {
            return this.context === CARLIE_CONTEXT_LIVE_HANDOFF;
        },
        primaryTopic(): any {
            return this.analysis?.primary_topic || null;
        },
        promises(): any[] {
            return carliePromises(this.analysis);
        },
        secondaryTopics(): any[] {
            return this.analysis?.secondary_topics || [];
        },
        sentiment(): any {
            return this.analysis?.sentiment || null;
        },
        // The sentiment item asks whether the customer was positive, so a flagged one is negative.
        sentimentDisplay(): { color: string, icon: string, label: string } | null {
            if (!this.sentiment) {
                return null;
            }
            if (this.sentiment.is_flagged) {
                return { color: "var(--error)", icon: "mdi-emoticon-sad-outline", label: "Negative sentiment" };
            }
            return { color: "var(--success)", icon: "mdi-emoticon-happy-outline", label: "Positive sentiment" };
        },
        sentimentSummary(): string {
            return carlieItemSummary(this.sentiment?.summary);
        },
        summary(): string {
            return carlieSummary(this.analysis, this.details?.handoff);
        },
        // System turns carry Carlie's prompt and are never shown.
        turns(): any[] {
            return (this.details?.turns || [])
                .filter((turn) => turn.role !== "system")
                .sort((a, b) => a.sequence_num - b.sequence_num);
        },
    },
    components: {
        CarlieOfferBrief,
        VolieAudio,
    },
    props: {
        aiCommunicationId: {
            type: Number,
            required: true,
        },
        context: {
            type: String,
            required: true,
        },
        // Details already fetched elsewhere, such as by the live transfer offer, shown without a refetch.
        initialDetails: {
            type: Object,
            default: null,
        },
        // Pushed to the agent on a live transfer when Carlie's analysis finishes, so it replaces the
        // analysis loaded with the details.
        liveAnalysis: {
            type: Object,
            default: null,
        },
        // Left empty where the recording already shows outside the tab, such as the missed call dialog.
        recordingUrl: {
            type: String,
            default: "",
        },
    },
    watch: {
        aiCommunicationId() {
            this.load();
        },
        turns(newValue, oldValue) {
            if (this.isLiveHandoff && newValue.length !== oldValue.length) {
                this.scrollConversationToEnd();
            }
        },
    },
    created() {
        this.load();
    },
    mounted() {
        if (this.isLiveHandoff) {
            this.scrollConversationToEnd();
        }
    },
})
</script>

<style lang="scss" scoped>
    .section-title {
        display: flex;
        align-items: center;
        margin-top: 16px;
        margin-bottom: 4px;
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.6);
    }

    .summary-list {
        padding-left: 16px;
    }

    .tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    // A live transfer fills the tab's fixed height and only the conversation scrolls. Its minimum
    // height keeps a few turns visible; past that the tab itself scrolls.
    .carlie-details-live {
        display: flex;
        flex-direction: column;

        > * {
            flex-shrink: 0;
        }

        .conversation-live {
            flex: 1 1 0;
            min-height: 200px;
            overflow-y: auto;
        }
    }

    .turn {
        max-width: 85%;
        padding: 8px 12px;
        margin-bottom: 8px;
        border-radius: 8px;
        background-color: var(--off-white);
    }

    .turn-assistant {
        background-color: var(--carlie-purple-10);
    }

    .turn-user {
        margin-left: auto;
    }

    .turn-logo {
        height: 14px;
        width: 14px;
    }

    ::v-deep .v-expansion-panel-content__wrap {
        padding: 0;
    }
</style>
